import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { track } from "../lib/mixpanel"
import { planPointAt, planHeadingAt, nearestOnPlan, planToLatLng, latLngToPlan } from "../lib/plan_geometry"
import { fanPath } from "../lib/site_walk_route"

// An indoor walk on a floor plan (Phelipe 2026-09-25, Site Walks list #9:
// "most walks are inside, where there's no GPS"). Beside the video, like the
// map of an outdoor walk: the plan, the walk's path on it, a dot that follows
// the video, a fan the way the 360° view looks, and a click on the path walks
// from there.
//
// A walk gets there by being placed once: pick the plan (the site plan
// pop-up in "pick" mode, site_plan_controller), then move the video to where
// the walk starts and click where that is on the plan; the same at each turn
// and at the end. Straight lines between the marks are close enough - people
// walk corridors. Anyone on the project can place a walk or change its marks.
//
// Opened and closed with the viewer (site-walks:opened / :closed on window);
// a click asks the viewer to move the video with site-walk-plan:jump.
const PATH = "#fb7185"
const MARK = "#2563eb"
const FAN_RADIUS = 40
const CLICK_REACH = 0.03 // of the plan's width

export default class extends Controller {
  static targets = ["panel", "title", "map", "hint", "error", "placingBar", "saveButton", "undoButton", "changeButton", "invite"]
  static values = { projectId: Number, projectPhase: String, text: Object }

  connect() {
    this._follow = this._follow.bind(this)
  }

  disconnect() {
    this.hide()
  }

  show(event) {
    const { walk, video } = event.detail || {}
    this.hide()
    if (!walk || !video || !walk.plan_url) return
    this.walk = walk
    this.video = video
    video.addEventListener("timeupdate", this._follow)
    video.addEventListener("seeked", this._follow)
    if (walk.plan?.site_plan) this._view(walk.plan.site_plan, walk.plan.points)
    // No GPS track: most likely indoors - offer the floor plan.
    else if (!walk.route) this.inviteTarget.classList.remove("hidden")
  }

  hide() {
    if (this.video) {
      this.video.removeEventListener("timeupdate", this._follow)
      this.video.removeEventListener("seeked", this._follow)
    }
    this.map?.remove()
    this.map = null
    this.dot = null
    this.fan = null
    this.pathLayer = null
    this.walk = null
    this.video = null
    this.plan = null
    this.marks = []
    this.placing = false
    this._awaitingPick = false
    this.panelTarget.classList.add("hidden")
    this.inviteTarget.classList.add("hidden")
  }

  // ── Placing ─────────────────────────────────────────────────────────

  choose() {
    this._awaitingPick = true
    this.dispatch("open", { target: window, prefix: "site-plan", detail: { mode: "pick" } })
  }

  // The plan chosen in the pop-up (site-plan:picked).
  picked(event) {
    if (!this.walk || !this._awaitingPick || !event.detail?.plan) return
    this._awaitingPick = false
    this._place(event.detail.plan, [])
  }

  change() {
    if (!this.plan) return
    this._place(this.plan, this.marks.map((m) => m.slice()))
  }

  _place(plan, marks) {
    this.plan = plan
    this.marks = marks
    this.placing = true
    this.video?.pause()
    this.inviteTarget.classList.add("hidden")
    this._open(plan)
    this.placingBarTarget.classList.remove("hidden")
    this.changeButtonTarget.hidden = true
    this._error(null)
    this._redraw()
  }

  undo() {
    this.marks.pop()
    this._redraw()
  }

  cancel() {
    this.placing = false
    this.placingBarTarget.classList.add("hidden")
    const placed = this.walk?.plan
    if (placed?.site_plan) {
      this._view(placed.site_plan, placed.points)
    } else {
      this.map?.remove()
      this.map = null
      this.panelTarget.classList.add("hidden")
      this.inviteTarget.classList.remove("hidden")
    }
  }

  async save() {
    if (!this.walk || !this.plan || this.marks.length < 2) return
    this.saveButtonTarget.disabled = true
    this._error(null)
    try {
      const response = await fetch(this.walk.plan_url, {
        method: "PATCH",
        headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ site_plan_id: this.plan.id, points: this.marks })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || this.textValue.failed)
      this.walk.plan = data.plan
      track("Site Walk Placed On Plan", {
        project_id: this.projectIdValue, project_phase: this.projectPhaseValue, site_walk_id: this.walk.id,
        site_plan_id: this.plan.id, marks: this.marks.length
      })
      this.placing = false
      this.placingBarTarget.classList.add("hidden")
      this._view(data.plan.site_plan, data.plan.points)
    } catch (error) {
      this._error(error.message || this.textValue.failed)
    } finally {
      this.saveButtonTarget.disabled = false
    }
  }

  // ── Showing ─────────────────────────────────────────────────────────

  _view(plan, points) {
    this.plan = plan
    this.marks = (points || []).map((p) => p.slice())
    this.placing = false
    this._open(plan)
    this.placingBarTarget.classList.add("hidden")
    this.changeButtonTarget.hidden = false
    this._redraw()
    this._follow()
  }

  // The plan in the panel: a flat Leaflet map (pan and zoom a big drawing).
  _open(plan) {
    this.panelTarget.classList.remove("hidden")
    this.titleTarget.textContent = plan.label ? `${this.textValue.title} · ${plan.label}` : this.textValue.title
    this.aspect = plan.height / plan.width
    if (this.map && this._mapFor === plan.id) return
    this.map?.remove()
    this._mapFor = plan.id
    const bounds = [[0, 0], planToLatLng(1, 0, this.aspect)]
    const map = L.map(this.mapTarget, { crs: L.CRS.Simple, minZoom: -4, maxZoom: 5, zoomSnap: 0.25, attributionControl: false })
    L.imageOverlay(plan.image_url, bounds).addTo(map)
    map.createPane("planFacing").style.zIndex = 390
    map.on("click", (e) => this._clicked(e.latlng))
    this.map = map
    this.dot = null
    this.fan = null
    this.pathLayer = null
    const frame = () => map.fitBounds(bounds)
    frame()
    // Laid out while the viewer opens: measure again once it's on screen.
    requestAnimationFrame(() => {
      if (this.map !== map) return
      map.invalidateSize()
      frame()
    })
  }

  _redraw() {
    if (!this.map) return
    this.pathLayer?.remove()
    this.pathLayer = L.layerGroup().addTo(this.map)
    const sorted = this.marks.slice().sort((a, b) => a[0] - b[0])
    const line = sorted.map(([, x, y]) => planToLatLng(x, y, this.aspect))
    if (line.length >= 2) {
      L.polyline(line, { color: "#ffffff", weight: 7, opacity: 0.9, interactive: false }).addTo(this.pathLayer)
      L.polyline(line, { color: PATH, weight: 4, opacity: 1, interactive: false }).addTo(this.pathLayer)
    }
    if (this.placing) {
      sorted.forEach(([t, x, y], i) => {
        L.marker(planToLatLng(x, y, this.aspect), {
          icon: L.divIcon({
            className: "", iconSize: [22, 22], iconAnchor: [11, 11],
            html: `<div title="${this._clock(t)}" style="width:22px;height:22px;border-radius:9999px;background:${MARK};color:#fff;border:2px solid #fff;` +
                  `display:flex;align-items:center;justify-content:center;font:600 12px sans-serif;box-shadow:0 1px 3px rgba(0,0,0,.4)">${i + 1}</div>`
          }),
          interactive: false, keyboard: false
        }).addTo(this.pathLayer)
      })
      const next = this.marks.length === 0 ? this.textValue.place_first : this.textValue.place_next
      this.hintTarget.textContent = `${next} ${this.textValue.place_count.replace("__COUNT__", this.marks.length)}`
      this.saveButtonTarget.disabled = this.marks.length < 2
      this.undoButtonTarget.disabled = this.marks.length === 0
    } else {
      this.hintTarget.textContent = this.textValue.view_hint
    }
  }

  // The dot (and the view's fan) where the video is.
  _follow() {
    if (!this.map || !this.video || this.placing || this.marks.length < 2) return
    const at = planPointAt(this.marks, this.video.currentTime || 0)
    if (!at) return
    const here = planToLatLng(at[0], at[1], this.aspect)
    if (!this.dot) {
      this.dot = L.circleMarker(here, { radius: 7, color: "#ffffff", weight: 3, fillColor: PATH, fillOpacity: 1, interactive: false }).addTo(this.map)
    } else {
      this.dot.setLatLng(here)
    }
    this.fan?.setLatLng(here)
    this._turnFan()
  }

  // site-walks:facing - which way the 360° view looks. On the plan, "ahead"
  // is the way the path goes; the view's turn from there turns the fan.
  face(event) {
    this.facing = event.detail
    this._turnFan()
  }

  _turnFan() {
    const facing = this.facing
    if (!this.map || !this.dot || !facing || !Number.isFinite(facing.yaw)) {
      this.fan?.remove()
      this.fan = null
      return
    }
    const heading = planHeadingAt(this.marks, this.video?.currentTime || 0, this.aspect)
    if (heading == null) return
    const bearing = (((heading + (facing.startYaw || 0) - facing.yaw) % 360) + 360) % 360
    if (!this.fan) {
      const size = FAN_RADIUS * 2
      this.fan = L.marker(this.dot.getLatLng(), {
        pane: "planFacing", interactive: false, keyboard: false,
        icon: L.divIcon({
          className: "", iconSize: [size, size], iconAnchor: [FAN_RADIUS, FAN_RADIUS],
          html: `<div data-fan style="width:${size}px;height:${size}px;transform-origin:50% 50%"><svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" aria-hidden="true">` +
                `<path fill="#3b82f6" fill-opacity="0.35" stroke="#ffffff" stroke-opacity="0.85" stroke-width="1.5"/></svg></div>`
        })
      }).addTo(this.map)
    }
    const element = this.fan.getElement()
    const turner = element?.querySelector("[data-fan]")
    if (turner) turner.style.transform = `rotate(${bearing.toFixed(1)}deg)`
    element?.querySelector("path")?.setAttribute("d", fanPath(facing.spread || 90, FAN_RADIUS))
  }

  // Placing: a click marks where the walk is at this moment of the video.
  // Watching: a click on (or near) the path walks from there.
  _clicked(latlng) {
    const [x, y] = latLngToPlan(latlng.lat, latlng.lng, this.aspect)
    if (x < 0 || x > 1 || y < 0 || y > 1) return
    if (this.placing) {
      const at = Math.round((this.video?.currentTime || 0) * 1000) / 1000
      this.marks = this.marks.filter((m) => Math.abs(m[0] - at) > 0.05) // one mark per moment
      this.marks.push([at, Math.round(x * 1e5) / 1e5, Math.round(y * 1e5) / 1e5])
      this._redraw()
      return
    }
    const hit = nearestOnPlan(this.marks, x, y)
    if (!hit || hit.reach > CLICK_REACH) return
    this.dispatch("jump", { detail: { at: hit.seconds } })
  }

  _error(message) {
    this.errorTarget.textContent = message || ""
    this.errorTarget.classList.toggle("hidden", !message)
  }

  _clock(seconds) {
    const s = Math.max(0, Math.round(seconds))
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
  }
}
